import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    type StoredJsonRowBoundary,
} from '../../stored-json-contract.types';
import {
    GatewayLifecycleResponseType,
    GatewayLifecycleStructuredResponseTypes,
    type CardPointeRawGatewayLifecycleResponseType,
    type CloverRawGatewayLifecycleResponseType,
    type GatewayLifecycleDiagnosticResponseType,
    type CloverWebhookPaymentRetrievalResponseSnapshot,
    type GatewayLifecycleResponse,
    type GatewayLifecycleResponseJsonBody,
    type ProviderGatewayResponseSnapshot,
    type ValorCardMetadataSnapshot,
    type ValorTaxComponentSnapshot,
    type ValorTaxDetailsSnapshot,
    type ValorTxnFetchGatewayResponseSnapshot,
    type ValorTxnFetchRecordSnapshot,
    type ValorVcStatusGatewayResponseSnapshot,
    type ValorRawGatewayLifecycleResponseType,
} from '../../types/ipg/gateway-lifecycle-response.types';
import type { JsonObject, JsonValue } from '../../common.types';
import {
    JsonLosslessNumberInputSchema,
    JsonObjectSchema,
    JsonValueSchema,
} from '../common.schemas';
import {
    extractStoredJsonNumberToken,
    defineSafeIntegerDomain,
    normalizeStoredJsonLosslessNumberFieldsAtPath,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    StoredJsonNumberRecordSchema,
    StoredJsonNumberSchema,
    StoredJsonObjectSchema,
    StoredJsonValueSchema,
} from '../../stored-json-number';
import { CloverCnpTokenSaleChargeBodySchema } from './clover/clover-cnp-token-sale.schemas';
import { CloverLifecycleFinalizationResponsePayloadSchema } from './clover/clover-lifecycle-finalization.schemas';
import { CloverLifecyclePreflightResponsePayloadSchema } from './clover/clover-lifecycle-preflight.schemas';
import { CloverRefundResponseDetailsSchema } from './clover/clover-refund.schemas';
import { CloverRefundKind } from '../../types/ipg/clover/clover-refund.types';
import { CloverTerminalSaleResponseLifecyclePayloadSchema } from './clover/clover-terminal-sale-lifecycle.schemas';
import {
    CloverTransactionDetailsPaymentBodySchema,
    StoredCloverTransactionDetailsPaymentBodySchema,
} from './clover/clover-transaction-details.schemas';
import { CloverVoidResponseDetailsSchema } from './clover/clover-void.schemas';

const CloverMinorUnitMaximum = 999999999;
const HttpStatusMinimum = 100;
const HttpStatusMaximum = 599;

const ValorTxnFetchRecordCountSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-response.valor-txnfetch.number_of_records',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'ValorTxnFetchGatewayResponseSnapshot.number_of_records',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const CloverMinorUnitSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-response.clover.minor-unit-amount',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover gateway response amount fields',
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    },
});

const CloverPositiveMinorUnitSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-response.clover.positive-minor-unit-amount',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover approved payment amount fields',
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    },
});

const CloverTimestampSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-response.clover.timestamp',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover gateway response timestamp fields',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const HttpStatusSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-response.http-status',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Gateway response details HTTP status',
        minimum: HttpStatusMinimum,
        maximum: HttpStatusMaximum,
    },
});

function normalizeStoredNumberOrStringFields(value: unknown, fields: readonly string[]): unknown {
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }
    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const field of fields) {
        const numberResult = StoredJsonNumberSchema.safeParse(recordResult.data[field]);
        if (numberResult.success) {
            normalized[field] = extractStoredJsonNumberToken(numberResult.data);
        }
    }
    return normalized;
}

function normalizeStoredValorTaxComponent(value: unknown): unknown {
    const normalizedRate = normalizeStoredJsonLosslessNumberFieldsAtPath(value, {
        path: [],
        fields: ['rate'],
    });
    return normalizeStoredNumberOrStringFields(normalizedRate, ['amount', 'taxableSales']);
}

function normalizeStoredValorTxnFetchRecord(value: unknown): unknown {
    const normalizedRecord = normalizeStoredNumberOrStringFields(value, ['TOTAL_TAX']);
    const recordResult = StoredJsonNumberRecordSchema.safeParse(normalizedRecord);
    const taxDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        recordResult.success ? recordResult.data.TAX_DETAILS : undefined
    );
    if (!recordResult.success || !taxDetailsResult.success) {
        return normalizedRecord;
    }
    const normalizedTaxDetails: Record<string, unknown> = {
        ...taxDetailsResult.data,
    };
    for (const field of ['stateTax', 'cityTax', 'reducedTax']) {
        if (Object.hasOwn(taxDetailsResult.data, field)) {
            normalizedTaxDetails[field] = normalizeStoredValorTaxComponent(
                taxDetailsResult.data[field]
            );
        }
    }
    return {
        ...recordResult.data,
        TAX_DETAILS: normalizedTaxDetails,
    };
}

function normalizeStoredValorTxnFetchResponse(value: unknown): unknown {
    const normalizedCount = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: ValorTxnFetchRecordCountSafeIntegerDomain,
        path: [],
        fields: ['number_of_records'],
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedCount);
    if (!rootResult.success || !Array.isArray(rootResult.data.data)) {
        return normalizedCount;
    }
    return {
        ...rootResult.data,
        data: rootResult.data.data.map(normalizeStoredValorTxnFetchRecord),
    };
}

export const StoredProviderGatewayResponseSnapshotSchema: z.ZodType<ProviderGatewayResponseSnapshot> =
    z.custom<ProviderGatewayResponseSnapshot>(
        (value: unknown): value is ProviderGatewayResponseSnapshot =>
            StoredJsonObjectSchema.safeParse(value).success,
        { message: 'Invalid stored provider gateway response snapshot' }
    );

function normalizeStoredCloverChargeResponse(value: unknown): unknown {
    const normalizedAmounts = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
        path: [],
        fields: ['amount', 'amount_refunded'],
    });
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedAmounts, {
        safeIntegerDomain: CloverTimestampSafeIntegerDomain,
        path: [],
        fields: ['created'],
    });
}

function normalizeStoredCloverPreflightResponse(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
        path: ['response_details', 'normalized_result'],
        fields: ['amount', 'tip_amount'],
    });
}

function normalizeStoredCloverTypedResponseDetails(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: HttpStatusSafeIntegerDomain,
        path: [],
        fields: ['httpStatus'],
    });
}

function normalizeStoredCloverTerminalSaleResponse(value: unknown): unknown {
    const normalizedStatus = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: HttpStatusSafeIntegerDomain,
        path: ['response_details'],
        fields: ['httpStatus'],
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    const responseDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.response_details : undefined
    );
    if (
        !responseDetailsResult.success ||
        (responseDetailsResult.data.outcome !== 'approved' &&
            responseDetailsResult.data.outcome !== 'declined')
    ) {
        return normalizedStatus;
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
        safeIntegerDomain: CloverPositiveMinorUnitSafeIntegerDomain,
        path: ['response_details', 'body', 'payment'],
        fields: ['amount'],
    });
}

function normalizeStoredCloverRefundBody(value: unknown): unknown {
    const normalized = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
        path: [],
        fields: ['amount', 'taxAmount', 'tipAmount'],
    });
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalized, {
        safeIntegerDomain: CloverTimestampSafeIntegerDomain,
        path: [],
        fields: ['created', 'createdTime'],
    });
}

function normalizeStoredCloverRefundResponse(value: unknown): unknown {
    const normalizedStatusWithoutKind = normalizeStoredCloverTypedResponseDetails(value);
    const normalizedStatusResult = StoredJsonNumberRecordSchema.safeParse(
        normalizedStatusWithoutKind
    );
    const normalizedStatus =
        normalizedStatusResult.success && normalizedStatusResult.data.kind === undefined
            ? {
                  ...normalizedStatusResult.data,
                  kind: CloverRefundKind.CLOUD_PAY,
              }
            : normalizedStatusWithoutKind;
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    if (!rootResult.success || rootResult.data.outcome !== 'refunded') {
        return normalizedStatus;
    }
    const bodyResult = StoredJsonNumberRecordSchema.safeParse(rootResult.data.body);
    if (!bodyResult.success) {
        return normalizedStatus;
    }
    if (rootResult.data.kind === CloverRefundKind.ECOMMERCE) {
        return {
            ...rootResult.data,
            body: normalizeStoredCloverRefundBody(bodyResult.data),
        };
    }

    const paymentResult = StoredJsonNumberRecordSchema.safeParse(bodyResult.data.payment);
    const normalizedPaymentResult = StoredJsonNumberRecordSchema.safeParse(
        paymentResult.success ? normalizeStoredCloverRefundBody(paymentResult.data) : undefined
    );
    const normalizedPayment =
        paymentResult.success && normalizedPaymentResult.success
            ? {
                  ...normalizedPaymentResult.data,
                  refunds: Array.isArray(paymentResult.data.refunds)
                      ? paymentResult.data.refunds.map((refund: unknown): unknown =>
                            normalizeStoredCloverRefundBody(refund)
                        )
                      : paymentResult.data.refunds,
              }
            : bodyResult.data.payment;

    return {
        ...rootResult.data,
        body: {
            ...bodyResult.data,
            payment: normalizedPayment,
            refund: normalizeStoredCloverRefundBody(bodyResult.data.refund),
        },
    };
}

function normalizeStoredCloverVoidResponse(value: unknown): unknown {
    const normalizedStatus = normalizeStoredCloverTypedResponseDetails(value);
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedStatus);
    if (
        !rootResult.success ||
        (rootResult.data.outcome !== 'voided' && rootResult.data.outcome !== 'refunded')
    ) {
        return normalizedStatus;
    }
    const bodyResult = StoredJsonNumberRecordSchema.safeParse(rootResult.data.body);
    if (bodyResult.success && bodyResult.data.object === 'refund') {
        const normalizedAmount = normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
            safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
            path: ['body'],
            fields: ['amount'],
        });
        return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedAmount, {
            safeIntegerDomain: CloverTimestampSafeIntegerDomain,
            path: ['body'],
            fields: ['created'],
        });
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedStatus, {
        safeIntegerDomain: CloverPositiveMinorUnitSafeIntegerDomain,
        path: ['body', 'payment'],
        fields: ['amount'],
    });
}

function normalizeStoredCloverGatewayResponsePayload(
    responseType: unknown,
    payload: unknown
): unknown {
    switch (responseType) {
        case GatewayLifecycleResponseType.CLOVER_CHARGE:
            return normalizeStoredCloverChargeResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT:
            return normalizeStoredCloverPreflightResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION:
        case GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR:
        case GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR:
        case GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON:
            return payload;
        case GatewayLifecycleResponseType.CLOVER_PAYMENT:
        case GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL: {
            const paymentResult =
                StoredCloverTransactionDetailsPaymentBodySchema.safeParse(payload);
            return paymentResult.success ? paymentResult.data : payload;
        }
        case GatewayLifecycleResponseType.CLOVER_REFUND:
            return normalizeStoredCloverRefundResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE:
            return normalizeStoredCloverTerminalSaleResponse(payload);
        case GatewayLifecycleResponseType.CLOVER_VOID:
            return normalizeStoredCloverVoidResponse(payload);
        default:
            return payload;
    }
}

export function normalizeStoredGatewayLifecycleResponseNumericLeaves(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    const normalizedPayload =
        rootResult.data.gateway_response_type === GatewayLifecycleResponseType.VALOR_TXN_FETCH
            ? normalizeStoredValorTxnFetchResponse(rootResult.data.gateway_response_json)
            : normalizeStoredCloverGatewayResponsePayload(
                  rootResult.data.gateway_response_type,
                  rootResult.data.gateway_response_json
              );
    if (normalizedPayload === rootResult.data.gateway_response_json) {
        return value;
    }
    return {
        ...rootResult.data,
        gateway_response_json: normalizedPayload,
    };
}

export const StoredCloverChargeResponseSchema: z.ZodType<
    z.output<typeof CloverCnpTokenSaleChargeBodySchema>
> = z.preprocess(normalizeStoredCloverChargeResponse, CloverCnpTokenSaleChargeBodySchema);

export const StoredCloverLifecyclePreflightResponseSchema: z.ZodType<
    z.output<typeof CloverLifecyclePreflightResponsePayloadSchema>
> = z.preprocess(
    normalizeStoredCloverPreflightResponse,
    CloverLifecyclePreflightResponsePayloadSchema
);

export const StoredCloverLifecycleFinalizationResponseSchema: z.ZodType<
    z.output<typeof CloverLifecycleFinalizationResponsePayloadSchema>
> = CloverLifecycleFinalizationResponsePayloadSchema;

export const StoredCloverTerminalSaleResponseSchema: z.ZodType<
    z.output<typeof CloverTerminalSaleResponseLifecyclePayloadSchema>
> = z.preprocess(
    normalizeStoredCloverTerminalSaleResponse,
    CloverTerminalSaleResponseLifecyclePayloadSchema
);

export const StoredCloverRefundResponseSchema: z.ZodType<
    z.output<typeof CloverRefundResponseDetailsSchema>
> = z.preprocess(normalizeStoredCloverRefundResponse, CloverRefundResponseDetailsSchema);

export const StoredCloverVoidResponseSchema: z.ZodType<
    z.output<typeof CloverVoidResponseDetailsSchema>
> = z.preprocess(normalizeStoredCloverVoidResponse, CloverVoidResponseDetailsSchema);

type GatewayLifecycleResponseVariantSchema<
    TResponseType extends GatewayLifecycleResponseType,
    TGatewayResponseJsonSchema extends z.ZodType,
> = z.ZodObject<{
    gateway_response_type: z.ZodLiteral<TResponseType>;
    gateway_response_json: TGatewayResponseJsonSchema;
}>;

function gatewayLifecycleResponseVariantSchema<
    TResponseType extends GatewayLifecycleResponseType,
    TGatewayResponseJsonSchema extends z.ZodType,
>(
    gatewayResponseType: TResponseType,
    gatewayResponseJsonSchema: TGatewayResponseJsonSchema
): GatewayLifecycleResponseVariantSchema<TResponseType, TGatewayResponseJsonSchema> {
    return z.object({
        gateway_response_type: z.literal(gatewayResponseType),
        gateway_response_json: gatewayResponseJsonSchema,
    });
}

export const GatewayLifecycleResponseTypeSchema: z.ZodType<GatewayLifecycleResponseType> = z.union([
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_HTTP_ERROR),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_HTTP_ERROR_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_INVALID_JSON),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_FUNDING),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_FUNDING_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_BROWSER_TOKENIZATION),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_BROWSER_TOKENIZATION_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_HOSTED_IFRAME_TOKENIZATION),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_HOSTED_IFRAME_TOKENIZATION_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_BY_ORDER_ID),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_BY_ORDER_ID_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_LIFECYCLE_FINALIZATION),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_LIFECYCLE_PREFLIGHT),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_REFUND),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_REFUND_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_SALE),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_SALE_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_TOKENIZATION_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_CANCEL_V2),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_VOID),
    z.literal(GatewayLifecycleResponseType.CARDPOINTE_VOID_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE),
    z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_BROWSER_TOKENIZATION),
    z.literal(GatewayLifecycleResponseType.CLOVER_BROWSER_TOKENIZATION_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR),
    z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON),
    z.literal(GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION),
    z.literal(GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR),
    z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL),
    z.literal(GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_REFUND),
    z.literal(GatewayLifecycleResponseType.CLOVER_REFUND_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE),
    z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE_V2),
    z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_TOKENIZATION),
    z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_CANCEL),
    z.literal(GatewayLifecycleResponseType.CLOVER_VOID),
    z.literal(GatewayLifecycleResponseType.CLOVER_VOID_V2),
    z.literal(GatewayLifecycleResponseType.GATEWAY_TERMINAL_SALE),
    z.literal(GatewayLifecycleResponseType.VALOR_RECONCILE),
    z.literal(GatewayLifecycleResponseType.VALOR_RECONCILE_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_REFUND),
    z.literal(GatewayLifecycleResponseType.VALOR_REFUND_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_BROWSER_TOKENIZATION),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_HTTP_ERROR),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_HTTP_ERROR_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON),
    z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_LIST),
    z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_LIST_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_STATUS),
    z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_STATUS_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_TXN_FETCH),
    z.literal(GatewayLifecycleResponseType.VALOR_TXN_FETCH_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_VC_CANCEL),
    z.literal(GatewayLifecycleResponseType.VALOR_VC_PUBLISH),
    z.literal(GatewayLifecycleResponseType.VALOR_VC_PUBLISH_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_VC_STATUS),
    z.literal(GatewayLifecycleResponseType.VALOR_VC_STATUS_V2),
    z.literal(GatewayLifecycleResponseType.VALOR_VOID),
    z.literal(GatewayLifecycleResponseType.VALOR_VOID_V2),
]);

export const ProviderGatewayResponseSnapshotSchema: z.ZodType<ProviderGatewayResponseSnapshot> =
    JsonObjectSchema;

export const CardPointeRawGatewayLifecycleResponseTypeSchema: z.ZodType<CardPointeRawGatewayLifecycleResponseType> =
    z.union([
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_AUTH_HTTP_ERROR_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_BROWSER_TOKENIZATION_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_FUNDING_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_HOSTED_IFRAME_TOKENIZATION_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_INQUIRE_BY_ORDER_ID_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_REFUND_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_CANCEL_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_SALE_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_TERMINAL_TOKENIZATION_V2),
        z.literal(GatewayLifecycleResponseType.CARDPOINTE_VOID_V2),
    ]);

export const CardPointeRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    z.object({
        gateway_response_type: CardPointeRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: JsonValueSchema,
    });

export const CloverRawGatewayLifecycleResponseTypeSchema: z.ZodType<CloverRawGatewayLifecycleResponseType> =
    z.union([
        z.literal(GatewayLifecycleResponseType.CLOVER_CHARGE_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_BROWSER_TOKENIZATION_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_REFUND_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE_V2),
        z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_TOKENIZATION),
        z.literal(GatewayLifecycleResponseType.CLOVER_TERMINAL_CANCEL),
        z.literal(GatewayLifecycleResponseType.CLOVER_VOID_V2),
    ]);

export const CloverRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    z.object({
        gateway_response_type: CloverRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: JsonValueSchema,
    });

export const ValorRawGatewayLifecycleResponseTypeSchema: z.ZodType<ValorRawGatewayLifecycleResponseType> =
    z.union([
        z.literal(GatewayLifecycleResponseType.VALOR_BROWSER_TOKENIZATION),
        z.literal(GatewayLifecycleResponseType.VALOR_RECONCILE_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_REFUND_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_HTTP_ERROR_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_LIST_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_TRANSACTION_STATUS_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_TXN_FETCH_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_VC_CANCEL),
        z.literal(GatewayLifecycleResponseType.VALOR_VC_PUBLISH_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_VC_STATUS_V2),
        z.literal(GatewayLifecycleResponseType.VALOR_VOID_V2),
    ]);

export const ValorRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> = z.object(
    {
        gateway_response_type: ValorRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: JsonValueSchema,
    }
);

const GatewayLifecycleDiagnosticResponseTypeSchema: z.ZodType<GatewayLifecycleDiagnosticResponseType> =
    z.custom<GatewayLifecycleDiagnosticResponseType>(
        (value) =>
            GatewayLifecycleResponseTypeSchema.safeParse(value).success &&
            !CardPointeRawGatewayLifecycleResponseTypeSchema.safeParse(value).success &&
            !CloverRawGatewayLifecycleResponseTypeSchema.safeParse(value).success &&
            !ValorRawGatewayLifecycleResponseTypeSchema.safeParse(value).success &&
            !GatewayLifecycleStructuredResponseTypes.some((responseType) => responseType === value),
        { message: 'Diagnostic gateway response type cannot use a semantic response type' }
    );

export const CloverWebhookPaymentRetrievalResponseSnapshotSchema: z.ZodType<CloverWebhookPaymentRetrievalResponseSnapshot> =
    z.object({ id: z.string().trim().min(1) }).catchall(JsonValueSchema);

export const StoredCloverWebhookPaymentRetrievalResponseSnapshotSchema: z.ZodType<CloverWebhookPaymentRetrievalResponseSnapshot> =
    z.custom<CloverWebhookPaymentRetrievalResponseSnapshot>(
        (value: unknown): value is CloverWebhookPaymentRetrievalResponseSnapshot => {
            const result = StoredJsonObjectSchema.safeParse(value);
            return result.success && z.string().trim().min(1).safeParse(result.data.id).success;
        },
        { message: 'Invalid stored Clover webhook payment retrieval response' }
    );

export const ValorCardMetadataSnapshotSchema: z.ZodType<ValorCardMetadataSnapshot> = z
    .object({
        CARD_BIN: z.string().optional(),
        CARD_TYPE: z.string().optional(),
        ISSUING_BANK: z.string().optional(),
        COUNTRY: z.string().optional(),
        CARD_LEVEL: z.string().optional(),
        CARD_CATEGORY: z.string().optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTaxComponentSnapshotSchema: z.ZodType<ValorTaxComponentSnapshot> = z
    .object({
        rate: JsonLosslessNumberInputSchema,
        amount: z.union([z.number(), z.string()]),
        taxableSales: z.union([z.number(), z.string()]),
    })
    .catchall(JsonValueSchema);

export const ValorTaxDetailsSnapshotSchema: z.ZodType<ValorTaxDetailsSnapshot> = z
    .object({
        stateTax: ValorTaxComponentSnapshotSchema.optional(),
        cityTax: ValorTaxComponentSnapshotSchema.optional(),
        reducedTax: ValorTaxComponentSnapshotSchema.optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTxnFetchRecordSnapshotSchema: z.ZodType<ValorTxnFetchRecordSnapshot> = z
    .object({
        REF_TXN_ID: z.string().optional(),
        TXN_ID: z.string().optional(),
        BASE_AMOUNT: z.string().optional(),
        NET_AMOUNT: z.string().optional(),
        CUSTOM_FEE_AMOUNT: z.string().optional(),
        TOTAL_TAX: z.union([z.string(), z.number()]).optional(),
        IS_VOID: z.string().optional(),
        IS_SETTLED: z.string().optional(),
        REFUND_AMOUNT: z.string().optional(),
        REFUND_COUNT: z.string().optional(),
        CARD_SCHEME: z.string().optional(),
        PAN: z.string().optional(),
        CARDHOLDER_NAME: z.string().optional(),
        AUTH_CODE: z.string().optional(),
        APPROVAL_CODE: z.string().optional(),
        RRN: z.string().optional(),
        STAN_NO: z.string().optional(),
        BATCH_NO: z.string().optional(),
        CARD_TYPE: z.string().optional(),
        CARD_METADATA: ValorCardMetadataSnapshotSchema.optional(),
        TAX_DETAILS: ValorTaxDetailsSnapshotSchema.optional(),
    })
    .catchall(JsonValueSchema);

export const ValorTxnFetchGatewayResponseSnapshotSchema: z.ZodType<ValorTxnFetchGatewayResponseSnapshot> =
    z
        .object({
            data: z.array(ValorTxnFetchRecordSnapshotSchema).min(1),
            error_no: z.string().optional(),
            status: z.string().optional(),
            number_of_records: z.number().optional(),
            meta: JsonObjectSchema.optional(),
        })
        .catchall(JsonValueSchema);

export const ValorVcStatusGatewayResponseSnapshotSchema: z.ZodType<ValorVcStatusGatewayResponseSnapshot> =
    z
        .object({
            response: z
                .object({
                    REQ_TXN_ID: z.string().optional(),
                    STATE: z.string().optional(),
                    EPI: z.string().optional(),
                    SERIAL_NO: z.string().optional(),
                    STAN_ID: z.string().optional(),
                    INVOICENUMBER: z.string().optional(),
                    INITIATOR_NAME: z.string().optional(),
                    AMOUNT: z.string().optional(),
                    PARTIAL: z.string().optional(),
                    TOTAL_AMOUNT: z.string().optional(),
                    ISSUER: z.string().optional(),
                    TRAN_TYPE: z.string().optional(),
                    MASKED_PAN: z.string().optional(),
                    RRN: z.string().optional(),
                    CODE: z.string().optional(),
                    BATCH_NO: z.string().optional(),
                    CARDHOLDER_NAME: z.string().optional(),
                    TXN_ID: z.string().optional(),
                    TRAN_METHOD: z.string().optional(),
                })
                .catchall(JsonValueSchema),
            error_no: z.string().optional(),
        })
        .catchall(JsonValueSchema);

export const GatewayLifecycleResponseJsonBodySchema: z.ZodType<GatewayLifecycleResponseJsonBody> =
    z.union([
        ValorTxnFetchGatewayResponseSnapshotSchema,
        ValorVcStatusGatewayResponseSnapshotSchema,
        ProviderGatewayResponseSnapshotSchema,
        JsonValueSchema,
    ]);

export const ValorTxnFetchGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_TXN_FETCH;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_TXN_FETCH,
    ValorTxnFetchGatewayResponseSnapshotSchema
);

export const ValorVcStatusGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_VC_STATUS;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_VC_STATUS,
    ValorVcStatusGatewayResponseSnapshotSchema
);

export const ValorSaleCopyInvalidJsonGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.VALOR_SALE_COPY_INVALID_JSON,
    z.null()
);

export const CloverChargeInvalidJsonGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_CHARGE_INVALID_JSON,
    z.null()
);

export const CloverChargeGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_CHARGE;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_CHARGE,
    CloverCnpTokenSaleChargeBodySchema
);

export const CloverPreflightGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT,
    CloverLifecyclePreflightResponsePayloadSchema
);

export const CloverFinalizationGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION,
    CloverLifecycleFinalizationResponsePayloadSchema
);

export const CloverPaymentGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_PAYMENT;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_PAYMENT,
    CloverTransactionDetailsPaymentBodySchema
);

export const CloverExternalPaymentGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL,
    CloverTransactionDetailsPaymentBodySchema
);

export const CloverRefundGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_REFUND;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_REFUND,
    CloverRefundResponseDetailsSchema
);

export const CloverTerminalSaleGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE,
    CloverTerminalSaleResponseLifecyclePayloadSchema
);

export const CloverVoidGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_VOID;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_VOID,
    CloverVoidResponseDetailsSchema
);

export const CloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema: z.ZodType<
    Extract<
        GatewayLifecycleResponse,
        {
            readonly gateway_response_type: typeof GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL;
        }
    >
> = gatewayLifecycleResponseVariantSchema(
    GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
    CloverWebhookPaymentRetrievalResponseSnapshotSchema
);

export const GatewayLifecycleDiagnosticResponseSchema: z.ZodType<
    Extract<GatewayLifecycleResponse, { readonly gateway_response_json: JsonObject }>
> = z.object({
    gateway_response_type: GatewayLifecycleDiagnosticResponseTypeSchema,
    gateway_response_json: ProviderGatewayResponseSnapshotSchema,
});

export const GatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> = z.union([
    ValorTxnFetchGatewayLifecycleResponseSchema,
    ValorVcStatusGatewayLifecycleResponseSchema,
    ValorSaleCopyInvalidJsonGatewayLifecycleResponseSchema,
    CloverChargeInvalidJsonGatewayLifecycleResponseSchema,
    CloverChargeGatewayLifecycleResponseSchema,
    CloverPreflightGatewayLifecycleResponseSchema,
    CloverFinalizationGatewayLifecycleResponseSchema,
    CloverPaymentGatewayLifecycleResponseSchema,
    CloverExternalPaymentGatewayLifecycleResponseSchema,
    CloverRefundGatewayLifecycleResponseSchema,
    CloverTerminalSaleGatewayLifecycleResponseSchema,
    CloverVoidGatewayLifecycleResponseSchema,
    CloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema,
    CardPointeRawGatewayLifecycleResponseSchema,
    CloverRawGatewayLifecycleResponseSchema,
    ValorRawGatewayLifecycleResponseSchema,
    GatewayLifecycleDiagnosticResponseSchema,
]);

export const StoredCloverChargeGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_CHARGE,
        StoredCloverChargeResponseSchema
    );

export const StoredCloverChargeHttpErrorGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_CHARGE_HTTP_ERROR,
        StoredProviderGatewayResponseSnapshotSchema
    );

export const StoredCloverPreflightGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_PREFLIGHT,
        StoredCloverLifecyclePreflightResponseSchema
    );

export const StoredCloverFinalizationGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_LIFECYCLE_FINALIZATION,
        StoredCloverLifecycleFinalizationResponseSchema
    );

export const StoredCloverPaymentGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT,
        StoredCloverTransactionDetailsPaymentBodySchema
    );

export const StoredCloverExternalPaymentGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL,
        StoredCloverTransactionDetailsPaymentBodySchema
    );

export const StoredCloverExternalPaymentHttpErrorGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_PAYMENT_EXTERNAL_HTTP_ERROR,
        StoredProviderGatewayResponseSnapshotSchema
    );

export const StoredCloverRefundGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_REFUND,
        StoredCloverRefundResponseSchema
    );

export const StoredCloverTerminalSaleGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_TERMINAL_SALE,
        StoredCloverTerminalSaleResponseSchema
    );

export const StoredCloverVoidGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_VOID,
        StoredCloverVoidResponseSchema
    );

export const StoredCloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL,
        StoredCloverWebhookPaymentRetrievalResponseSnapshotSchema
    );

export const StoredCloverRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    z.object({
        gateway_response_type: CloverRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: z.custom<JsonValue>(
            (value) => StoredJsonValueSchema.safeParse(value).success,
            { message: 'Invalid stored raw Clover gateway response snapshot' }
        ),
    });

export const StoredCardPointeRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    z.object({
        gateway_response_type: CardPointeRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: z.custom<JsonValue>(
            (value) => StoredJsonValueSchema.safeParse(value).success,
            { message: 'Invalid stored raw CardPointe gateway response snapshot' }
        ),
    });

export const StoredValorRawGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    z.object({
        gateway_response_type: ValorRawGatewayLifecycleResponseTypeSchema,
        gateway_response_json: z.custom<JsonValue>(
            (value) => StoredJsonValueSchema.safeParse(value).success,
            { message: 'Invalid stored raw Valor gateway response snapshot' }
        ),
    });

export const StoredValorTxnFetchGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> =
    gatewayLifecycleResponseVariantSchema(
        GatewayLifecycleResponseType.VALOR_TXN_FETCH,
        z.preprocess(
            normalizeStoredValorTxnFetchResponse,
            ValorTxnFetchGatewayResponseSnapshotSchema
        )
    );

export const StoredGatewayLifecycleResponseSchema: z.ZodType<GatewayLifecycleResponse> = z.union([
    StoredCardPointeRawGatewayLifecycleResponseSchema,
    StoredCloverRawGatewayLifecycleResponseSchema,
    StoredValorRawGatewayLifecycleResponseSchema,
    StoredValorTxnFetchGatewayLifecycleResponseSchema,
    StoredCloverChargeGatewayLifecycleResponseSchema,
    StoredCloverChargeHttpErrorGatewayLifecycleResponseSchema,
    CloverChargeInvalidJsonGatewayLifecycleResponseSchema,
    StoredCloverPreflightGatewayLifecycleResponseSchema,
    StoredCloverFinalizationGatewayLifecycleResponseSchema,
    StoredCloverPaymentGatewayLifecycleResponseSchema,
    StoredCloverExternalPaymentGatewayLifecycleResponseSchema,
    StoredCloverExternalPaymentHttpErrorGatewayLifecycleResponseSchema,
    StoredCloverRefundGatewayLifecycleResponseSchema,
    StoredCloverTerminalSaleGatewayLifecycleResponseSchema,
    StoredCloverVoidGatewayLifecycleResponseSchema,
    StoredCloverWebhookPaymentRetrievalGatewayLifecycleResponseSchema,
    GatewayLifecycleResponseSchema,
]);

export function normalizeGatewayLifecycleResponseStorageV1(
    response: GatewayLifecycleResponse
): GatewayLifecycleResponse {
    return response;
}

export const GatewayResponseJsonStorageContract = defineStoredJsonContract({
    contractName: 'GatewayResponseJson',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredGatewayLifecycleResponseSchema',
            schema: StoredGatewayLifecycleResponseSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                gateway_response_type: boundary.siblingValues.gateway_response_type,
                gateway_response_json: boundary.value,
            }),
            guards: [
                {
                    kind: 'sibling_field_present',
                    siblingField: 'gateway_response_type',
                },
            ],
            normalize: normalizeGatewayLifecycleResponseStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredGatewayLifecycleResponseRowParser: z.ZodType<GatewayLifecycleResponse> =
    buildStoredJsonParser(GatewayResponseJsonStorageContract);

export function parseGatewayLifecycleResponseJsonParts(params: {
    responseType: string;
    payload: unknown;
    context: string;
}): GatewayLifecycleResponse {
    const parseResult = GatewayLifecycleResponseSchema.safeParse({
        gateway_response_type: params.responseType,
        gateway_response_json: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_response_json for ${params.context} type ${params.responseType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}

export function parseStoredGatewayLifecycleResponseJsonParts(params: {
    responseType: string;
    payload: unknown;
    context: string;
}): GatewayLifecycleResponse {
    const parseResult = StoredGatewayLifecycleResponseSchema.safeParse({
        gateway_response_type: params.responseType,
        gateway_response_json: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid stored gateway_response_json for ${params.context} type ${params.responseType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}
